import React, { useMemo, useState } from "react";
import { Row, Col, Form, Select, Input, Button, Collapse, DatePicker } from "antd";
import type { FormInstance } from "antd";
import type { Moment } from "moment";
import type { RangeValue } from "rc-picker/lib/interface";
import { useAppSelector } from "../../..";
import type { AnyObject, DictionaryDto } from "../../../lib/interfaces";
import { checkIsLoading, displayFormat } from "../../../lib/utils";
import { IClientType, ISearchFilterField, LoadingStatusCode } from "../../../lib/types";
import { BulkFiltersToggler } from "../../BulkFiltersToggler/BulkFiltersToggler";
import { ActiveSearchFormItem, AgreementsSearchFormItem } from "../../BulkSearchItems";
import "./RejectedTrafficSearchFilters.less";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

interface ComponentProps {
  getDate: (values: RangeValue<Moment>) => void;
  resetSearchPaymentsAndResetSetSelectedRowKeys: () => void;
  rejectedTrafficsLoading: LoadingStatusCode;
  form: FormInstance;
  defaultSearchParams: AnyObject;
  onSearch: (values: AnyObject) => void;
  filtersCollapseState: string;
  setFiltersCollapsState: React.Dispatch<React.SetStateAction<string>>;
}

export const RejectedTrafficSearchFilters = (props: ComponentProps) => {
  const {
    getDate,
    resetSearchPaymentsAndResetSetSelectedRowKeys,
    rejectedTrafficsLoading,
    defaultSearchParams,
    form,
    onSearch,
    filtersCollapseState,
    setFiltersCollapsState,
  } = props;

  const trafficStatus = (useAppSelector(
    (state) => state.rtkDictionaries.trafficStatus
  ) as unknown) as DictionaryDto[];
  const { tenant } = useAppSelector((state) => state.tenants);

  const [clientType, setClientType] = useState<IClientType>("ORGANIZATION");

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Клиент",
        fieldElement: (
          <AgreementsSearchFormItem
            name="subscribersThroughSubscriberIds"
            combineAgreements
            clientType={clientType}
            setClientType={setClientType}
            multiselect
            onChange={() => {
              form.setFieldsValue({
                "organization.property.stringValue": undefined,
                subscribers: undefined,
              });
            }}
          />
        ),
        visible: true,
      },
      {
        label: "Причина отсева",
        fieldElement: (
          <Form.Item label="Причина отсева" name="includeServiceUsageStatusIds">
            <Select
              showSearch
              placeholder="Выбрать"
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              allowClear
              mode="multiple"
              showArrow
            >
              {trafficStatus &&
                trafficStatus
                  .filter((item) => item.id !== 0)
                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                  .map((item) => (
                    <Select.Option key={item.id} value={item?.id}>
                      {item?.name}
                    </Select.Option>
                  ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Период",
        fieldElement: (
          <Form.Item label="Период" name="rangeDate">
            <DatePicker.RangePicker
              onChange={getDate}
              format={displayFormat}
              style={{ width: "100%" }}
              allowClear={false}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: tenant?.id === 1 ? "БИН" : "ИНН",
        fieldElement: (
          <ActiveSearchFormItem
            name="organization.property.stringValue"
            onSelect={(props) => {
              form.setFieldsValue({
                subscribersThroughSubscriberIds: [props.accountId],
                subscribers: undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: false,
      },
      {
        label: "Тех. ID",
        labelTitle: "Технический ID",
        // fieldElement: (
        //   <Form.Item label="Тех. ID" name="subscribers">
        //     <Input placeholder="Введите номер" allowClear />
        //   </Form.Item>
        // ),
        fieldElement: (
          <ActiveSearchFormItem
            name="subscribers"
            onSelect={(props) => {
              form.setFieldsValue({
                subscribersThroughSubscriberIds: [props.accountId],
                "organization.property.stringValue": undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: true,
      },
    ],
    [tenant, trafficStatus, clientType]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapsState(newState);
  };

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  return (
    <div className="rejectedTraffic-searchFilters">
      <div className="inner-row">
        <Collapse
          defaultActiveKey="1"
          onChange={toggleFiltersCollapseState}
          expandIconPosition="right"
          className="filter-collapse"
        >
          <Collapse.Panel header={getPanelHeader()} key="1">
            <Form
              form={form}
              labelAlign="left"
              initialValues={defaultSearchParams}
              {...layout}
              onFinish={onSearch}
            >
              <Row justify="end">
                <BulkFiltersToggler
                  allFields={allFields}
                  setFields={setFields}
                  filterName="rejected-trarric"
                />
              </Row>
              <Row gutter={50}>
                {fields
                  .filter(({ visible }) => visible)
                  .map(({ fieldElement }, idx) => (
                    <Col span={8} key={idx}>
                      {fieldElement}
                    </Col>
                  ))}
              </Row>
              <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: "right", marginBottom: 0 }}>
                <Button onClick={resetSearchPaymentsAndResetSetSelectedRowKeys}>
                  Сброс параметров
                </Button>
                <Button
                  loading={checkIsLoading(rejectedTrafficsLoading)}
                  htmlType="submit"
                  type="primary"
                >
                  Применить
                </Button>
              </Form.Item>
            </Form>
          </Collapse.Panel>
        </Collapse>
      </div>
    </div>
  );
};
